import React from "react";
import { useDispatch } from "react-redux";
import { AsyncPaginate, LoadOptions } from "react-select-async-paginate";
import { RootDispatch } from "../../store";
import { fetchRawMaterialsThunk } from "../../store/slices/warehouseSlice";
import { IWarehouseRawMaterial } from "../../models/warehouseRawMaterial.model";

export interface MaterialOption {
  value: string;
  label: string;
  rawMaterial: IWarehouseRawMaterial;
}

interface MaterialAsyncSelectProps {
  value: IWarehouseRawMaterial | null;
  onChange: (material: IWarehouseRawMaterial | null) => void;
  from?: string;
  placeholder?: string;
  isDisabled?: boolean;
  isClearable?: boolean;
}

export const selectStyles = {
  control: (base: any) => ({
    ...base,
    minHeight: "42px",
    fontSize: "0.875rem",
    borderRadius: "0.5rem",
    borderColor: "#D1D5DB",
    boxShadow: "none",
    "&:hover": { borderColor: "#9CA3AF" },
  }),
  valueContainer: (base: any) => ({
    ...base,
    padding: "0 12px",
  }),
  menuPortal: (base: any) => ({
    ...base,
    zIndex: 9999,
  }),
};

const MaterialAsyncSelect: React.FC<MaterialAsyncSelectProps> = ({
  value,
  onChange,
  from = "factory",
  placeholder = "Search & select material...",
  isDisabled = false,
  isClearable = true,
}) => {
  const dispatch = useDispatch<RootDispatch>();

  const loadMaterialOptions: LoadOptions<
    MaterialOption,
    any,
    { page: number }
  > = async (searchQuery, _loadedOptions, additional) => {
    try {
      const currentPage = additional?.page || 1;
      const response = await dispatch(
        fetchRawMaterialsThunk({
          page: currentPage,
          limit: 10,
          search: searchQuery,
          from,
        }),
      ).unwrap();

      const options: MaterialOption[] = response.data.map((rm) => ({
        value: rm._id,
        label: rm.name,
        rawMaterial: rm,
      }));

      return {
        options,
        hasMore: currentPage < response.totalPages,
        additional: {
          page: currentPage + 1,
        },
      };
    } catch (error) {
      return {
        options: [],
        hasMore: false,
        additional: { page: 1 },
      };
    }
  };

  const selectedOption: MaterialOption | null = value
    ? {
        value: value._id,
        label: value.name,
        rawMaterial: value,
      }
    : null;

  return (
    <AsyncPaginate
      value={selectedOption}
      loadOptions={loadMaterialOptions}
      additional={{ page: 1 }}
      onChange={(opt: MaterialOption | null) => {
        onChange(opt ? opt.rawMaterial : null);
      }}
      styles={selectStyles}
      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
      placeholder={placeholder}
      debounceTimeout={300}
      isDisabled={isDisabled}
      isClearable={isClearable}
    />
  );
};

export default MaterialAsyncSelect;
